import * as permifyConstants from '@repo/api/permify/constants';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  CornerDownRight,
  Ellipsis,
  LoaderCircle,
  MessageCircle,
  Smile,
  Star,
} from 'lucide-react';

import { RatingAverageResponse } from '@repo/api/open-calls/graphql';
import AllScoreSortDropdown from '@repo/ui/components/custom/candidate/all-scores-sort-dropdown/component';
import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { cn } from '@repo/ui/lib/utils';
import { Reply, Review } from '@repo/ui/models/candidate';
import { useState } from 'react';
import CommentsLoading from '../loading/comments';
import Comment from './comment';
import allowEditSelfComment from '@/helpers/allow-edit-self-comment';
import { useComments } from '@/state/use-comments';
import { Textarea } from '@repo/ui/components/ui/textarea';
import EmojiPicker from 'emoji-picker-react';
import { CommentActions as CustomActions } from '@/components/candidates/comment.action';

dayjs.extend(relativeTime);

export type ReplyPayload = {
  reviewId: string;
  commentId: string;
  comment: string;
};

export type CandidateAllScoresProps = {
  reviews: Array<Review>;
  processing?: boolean;
  ratingAverage?: RatingAverageResponse | null;
  submissionId: string;
  userName: string;
  onCommentEditClick: (review: Review) => void;
  onReplayClick: (data: ReplyPayload) => void;
  onUpdateClick: (commentId: string, comment: string) => void;
};

export function CandidateAllScores({
  reviews,
  processing,
  ratingAverage,
  submissionId,
  userName,
  onCommentEditClick,
  onReplayClick,
  onUpdateClick,
}: CandidateAllScoresProps) {
  const extractRatingAverage = (average?: number) => {
    if (!average || average === 0) {
      return 0;
    }
    const fixed = average.toFixed(1);
    const spited = fixed.split('.');
    if (spited[1] === '0') {
      return spited[0];
    }
    return average.toFixed(1);
  };

  const [visibleReplyCommentId, setVisibleReplyCommentId] = useState<
    string | null
  >(null);

  return (
    <div className="grid gap-2">
      {/* <CandidateAverageScore /> */}
      <div className="grid gap-4 bg-white p-4">
        <div className="font-bold ">Average Score</div>
        <div>
          <div className="flex items-center gap-1">
            {processing && <LoaderCircle className="animate-spin" />}
            {!processing && (
              <span className="font-bold text-3xl">
                {extractRatingAverage(ratingAverage?.average)}
              </span>
            )}
            /5
          </div>
          <div className="text-xs flex items-center mt-2 gap-1">
            <Star size="12" />{' '}
            {processing && <LoaderCircle size={10} className="animate-spin" />}
            {!processing && (
              <span>{ratingAverage?.count ?? 0}</span>
            )} rating
            {(ratingAverage?.count ?? 0 > 1) ? 's' : ''} in total
          </div>
        </div>
      </div>

      <div className="flex justify-between p-4 bg-white">
        <div className="flex items-center font-bold">Scores</div>
        <AllScoreSortDropdown
          submissionId={submissionId}
          dataPermission={permifyConstants.permissions.openCalls.moveCandidates}
        />
      </div>

      <CandidateReviews reviews={reviews} processing={processing} />
    </div>
  );

  function CandidateReviews({
    reviews,
    processing,
  }: {
    reviews: Array<Review>;
    processing?: boolean;
  }) {
    const replies = reviews.filter((review) => review.parentID);
    const reviewsWithReplies = reviews
      .filter((x) => !x.parentID)
      .map((review) => ({
        ...review,
        replies: replies.filter((reply) => reply.parentID === review.commentId),
      }));
    return (
      <div className="grid">
        {processing && <CommentsLoading />}
        {!processing &&
          reviewsWithReplies.map((review) => (
            <CandidateReview review={review} key={review.id} />
          ))}
      </div>
    );

    function CandidateReview({ review }: { review: Review }) {
      return (
        <div>
          <CandidateComment review={review} />
          <CandidateReviewReplies replies={review.replies} />
        </div>
      );

      function CandidateComment({ review }: { review: Review }) {
        const isAllowEditComment = allowEditSelfComment(review.createdAt);

        return (
          <>
            <div className="bg-white grid gap-2 px-4 py-2 border-b border-zinc-200">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">{review.jury}</span>{' '}
                  <span className="text-xs">
                    {dayjs(review.createdAt).fromNow(true)} ago
                  </span>
                </div>

                <CommentActions
                  disabled={review.jury !== userName || !isAllowEditComment}
                  onCommentEditClick={() => onCommentEditClick(review)}
                />
              </div>

              <div className="grid gap-1">
                <CandidateCommentRating rating={review.rating} />
                <p>{review.comment}</p>
                <div className="flex justify-end">
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      onClick={() => setVisibleReplyCommentId(review.commentId)}
                    >
                      <MessageCircle />{' '}
                      <span className="font-medium text-sm">Reply</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {visibleReplyCommentId &&
              visibleReplyCommentId === review.commentId && (
                <div className="grid gap-2 bg-white p-4 border-b border-zinc-200">
                  <Comment
                    useTooltip={false}
                    onUpdateClick={onUpdateClick}
                    title="Add a reply as:"
                    saveButtonText="Reply"
                    saveButtonIcon={<MessageCircle />}
                    cancelButtonText="Discard"
                    onCommentAddClick={(_, comment) => {
                      onReplayClick({
                        commentId: review.commentId,
                        comment: comment ?? '',
                        reviewId: review.id,
                      });
                      setVisibleReplyCommentId(null);
                    }}
                    onCancelClick={() => setVisibleReplyCommentId(null)}
                    processing={processing}
                    userName={userName}
                    isCommentClosed={false}
                  />
                </div>
              )}
          </>
        );
      }

      function CandidateReviewReplies({ replies }: { replies: Array<Reply> }) {
        return (
          <div className="grid">
            {replies.map((reply) => (
              <CandidateCommentReply reply={reply} key={reply.id} />
            ))}
          </div>
        );
      }
    }

    function CandidateCommentReply({ reply }: { reply: Reply }) {
      const { updateComment } = useComments();
      const [inEditMode, setInEditMode] = useState(false);
      const [comment, setComment] = useState(reply.comment);
      const isAllowEditReply = allowEditSelfComment(reply.createdAt);
      return (
        <div className="bg-neutral-100 p-4 border-b border-zinc-200">
          <div
            className={cn('flex justify-between ml-4', {
              'flex-col gap-2': inEditMode,
            })}
          >
            <div className="flex gap-2">
              <div className="flex items-center justify-center size-6">
                <CornerDownRight size={16} />
              </div>
              <div className="grid w-full">
                <div>
                  <span className="font-bold">{reply.jury}</span>{' '}
                  <span className="text-xs">
                    {dayjs(reply.createdAt).fromNow(true)} ago
                  </span>
                </div>
                {!inEditMode && <div className="text-xs">{reply.comment}</div>}
                {inEditMode && (
                  <div className="relative">
                    <Textarea
                      value={comment}
                      onInput={(e) => setComment(e.currentTarget.value)}
                      className="pr-10 min-h-[80px] text-base"
                    />
                    <div className="absolute right-4 bottom-4 text-gray-400 cursor-pointer hover:text-gray-600">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Smile size={16} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <EmojiPicker
                            onEmojiClick={(emoji) => {
                              setComment((prev) => prev + emoji.emoji);
                            }}
                          />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!inEditMode && (
              <CommentActions
                disabled={reply.jury !== userName || !isAllowEditReply}
                onCommentEditClick={() => setInEditMode(true)}
              />
            )}
            {inEditMode && (
              <CustomActions
                saveButtonText="Update"
                cancelButtonText="Cancel"
                saveButtonIcon={<MessageCircle />}
                onSaveClick={() => {
                  updateComment(
                    (reply as unknown as ReplyPayload).commentId,
                    comment,
                  ).then(() => setInEditMode(false));
                }}
                onCancelClick={() => setInEditMode(false)}
                processing={processing}
                disabled={!isAllowEditReply}
              />
            )}
          </div>
        </div>
      );
    }
  }

  function CandidateCommentRating({ rating }: { rating: number }) {
    const stars = [];
    const fullStars = Math.floor(rating);

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star size="12" key={i} fill="black" />);
    }

    return <div className="flex gap-1">{stars}</div>;
  }

  function CommentActions({
    onCommentEditClick,
    disabled,
  }: {
    onCommentEditClick: () => void;
    disabled?: boolean;
  }) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild className={cn({ hidden: disabled })}>
          <Button variant="ghost">
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={onCommentEditClick}>Edit</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
}
